import React, { useState } from "react";
import RailTag from "../../components/terminal/RailTag.jsx";
import { fmtCcy } from "../../components/terminal/format.js";
import useTerminalReceive from "./useTerminalReceive.js";

/* Receive — Channels-first inbox. A channel = named bundle of
   receiving accounts (BANK / WALLET / CARD) for one client.
   Per BACKEND_TERMINAL_SPEC §C, the channel object now carries
   accounts[]; until C.1 lands the FE renders synthetic accounts[]
   via the adapter below so the layout works on existing data. */

const MOCK_CHANNELS = [
  { id:"CH-001", client:"Mitsubishi Trading", region_iso:"JP", status:"ACTIVE", label:"Yen invoices · USDC settlement",
    vol_30d_usd_eq:"4820000", received_30d_count:14, last_seen_iso:"2m ago",
    note:"Auto-FX JPY → USDC daily at 16:00 JST",
    accounts:[
      { id:"A1", kind:"BANK",   currency:"JPY",  label:"JPY · Zengin",     institution:"MUFG",     number_masked:"·••• 0021", routing:"0005-001",  balance:"84120000", primary:true },
      { id:"A2", kind:"BANK",   currency:"USD",  label:"USD · ACH/Wire",   institution:"JPMorgan", number_masked:"·••• 4421", routing:"021000021", balance:"1240000" },
      { id:"A3", kind:"WALLET", currency:"USDC", label:"Ethereum · USDC",  institution:"On-chain", number_masked:"0x82a1·••• 1b4e", routing:"EVM·1", balance:"5240900" },
      { id:"A4", kind:"CARD",   currency:"USD",  label:"Card · Stripe",    institution:"Stripe",   number_masked:"acct_1NkPm…", routing:"card",     balance:"218400" },
    ],
  },
  { id:"CH-002", client:"Vinci Construction", region_iso:"FR", status:"ACTIVE", label:"EUR retainer · monthly",
    vol_30d_usd_eq:"1538800", received_30d_count:6, last_seen_iso:"18m ago",
    note:"SEPA-Instant preferred · auto-allocate to Project K88",
    accounts:[
      { id:"A1", kind:"BANK", currency:"EUR", label:"EUR · SEPA",        institution:"Deutsche", number_masked:"DE89·••• 2210", routing:"DEUTDEFF", balance:"4120490", primary:true },
      { id:"A2", kind:"BANK", currency:"GBP", label:"GBP · Faster Pmts", institution:"Barclays", number_masked:"·••• 8841",     routing:"20-00-00", balance:"1204018" },
    ],
  },
  { id:"CH-003", client:"Asia Logistics Group", region_iso:"SG", status:"ACTIVE", label:"Stablecoin-only · weekly",
    vol_30d_usd_eq:"1840000", received_30d_count:4, last_seen_iso:"46m ago",
    note:"USDT TRON only · sweep to USDC Ethereum",
    accounts:[
      { id:"A1", kind:"WALLET", currency:"USDT", label:"TRON · USDT",   institution:"On-chain", number_masked:"TXa9bF·••• gVtFsE", routing:"TRC20", balance:"894120", primary:true },
      { id:"A2", kind:"WALLET", currency:"USDC", label:"Solana · USDC", institution:"On-chain", number_masked:"8KxPm3·••• yMnA",   routing:"SOL",   balance:"1120400" },
    ],
  },
  { id:"CH-004", client:"Helios Foundation", region_iso:"US", status:"ACTIVE", label:"Grants · multi-rail",
    vol_30d_usd_eq:"924000", received_30d_count:3, last_seen_iso:"1h 22m",
    note:"FedNow + Wire fallback · memo-tagged GRANT-K42",
    accounts:[
      { id:"A1", kind:"BANK", currency:"USD", label:"USD · FedNow", institution:"JPMorgan", number_masked:"·••• 4421", routing:"021000021", balance:"12440212", primary:true },
    ],
  },
  { id:"CH-006", client:"Greystone Property", region_iso:"US", status:"PAUSED", label:"Rent collection · suspended",
    vol_30d_usd_eq:"145000", received_30d_count:0, last_seen_iso:"12d ago",
    note:"Lease terminated · channel pending close",
    accounts:[
      { id:"A1", kind:"BANK", currency:"USD", label:"USD · ACH", institution:"JPMorgan", number_masked:"·••• 4421", routing:"021000021", balance:"0", primary:true },
    ],
  },
];

const MOCK_INCOMING = {
  "CH-001": [
    { rail:"SWIFT",  currency:"JPY",  amount:"4800000", status:"CLEARED", when:"10:24", reference:"INV-K228", account_label:"JPY · Zengin" },
    { rail:"USDC",   currency:"USDC", amount:"320000",  status:"CLEARED", when:"09:48", reference:"TX-A19",   account_label:"Ethereum · USDC" },
    { rail:"SWIFT",  currency:"JPY",  amount:"3200000", status:"CLEARED", when:"09:12", reference:"INV-K227", account_label:"JPY · Zengin" },
    { rail:"CARD",   currency:"USD",  amount:"218400",  status:"CLEARED", when:"08:44", reference:"STR-9X1",  account_label:"Card · Stripe" },
  ],
  "CH-002": [
    { rail:"SEPA-I", currency:"EUR", amount:"84500",  status:"CLEARED", when:"10:23", reference:"INV-V881", account_label:"EUR · SEPA" },
    { rail:"SEPA",   currency:"EUR", amount:"124800", status:"CLEARED", when:"08:12", reference:"INV-V880", account_label:"EUR · SEPA" },
  ],
  "CH-003": [
    { rail:"USDT",   currency:"USDT", amount:"320000", status:"CLEARED", when:"10:20", reference:"TX-A19", account_label:"TRON · USDT" },
  ],
  "CH-004": [
    { rail:"FedNow", currency:"USD", amount:"285000", status:"PENDING", when:"10:22", reference:"GRANT-K42", account_label:"USD · FedNow" },
  ],
  "CH-006": [],
};

function StatusPip({ status }) {
  const map = {
    ACTIVE: { c:"#2CCB7E", t:"ACTIVE" },
    PAUSED: { c:"#F5A524", t:"PAUSED" },
    DRAFT:  { c:"#8E97A8", t:"DRAFT"  },
    ARCHIVED: { c:"#4F5868", t:"ARCHIVED" },
  };
  const s = map[status] || map.DRAFT;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:9, letterSpacing:"0.14em", color:s.c, fontWeight:600 }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:s.c, boxShadow: status === "ACTIVE" ? `0 0 6px ${s.c}` : "none" }} />
      {s.t}
    </span>
  );
}

function KindPip({ kind }) {
  const map = {
    BANK:   { bg:"rgba(74,140,255,0.10)", c:"#4A8CFF", t:"BANK"   },
    WALLET: { bg:"rgba(44,203,126,0.10)", c:"#2CCB7E", t:"WALLET" },
    CARD:   { bg:"rgba(245,165,36,0.10)", c:"#F5A524", t:"CARD"   },
  };
  const s = map[kind] || map.BANK;
  return (
    <span style={{ display:"inline-block", padding:"2px 6px", fontSize:9, letterSpacing:"0.10em", fontWeight:600, color:s.c, background:s.bg, border:`1px solid ${s.c}33` }}>{s.t}</span>
  );
}

function FieldRow({ label, value, mono, copy }) {
  return (
    <div>
      <div style={{ fontSize:9, letterSpacing:"0.18em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>{label}</div>
      <div className={mono ? "mono" : ""} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"7px 10px", border:"1px solid var(--term-line)", background:"var(--term-bg-pane)", fontSize: mono ? 11.5 : 12.5 }}>
        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{value}</span>
        {copy && <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", cursor:"pointer", marginLeft:8, letterSpacing:"0.10em" }}>COPY</span>}
      </div>
    </div>
  );
}

function Stat({ label, value, mono, small }) {
  return (
    <div>
      <div style={{ fontSize:9, letterSpacing:"0.14em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600 }}>{label}</div>
      <div className="mono" style={{ fontSize: small ? 12 : 16, color:"var(--term-ink-1)", marginTop:2, letterSpacing:"-0.01em" }}>{value}</div>
    </div>
  );
}

export default function ReceiveScreen({ token }) {
  const live = useTerminalReceive(token);
  const channels = (live.channels && live.channels.length) ? live.channels : MOCK_CHANNELS;
  const incoming = (live.channels && live.channels.length) ? live.incoming : MOCK_INCOMING;
  const [chId, setChId] = useState(channels[0]?.id);
  const [acctIdx, setAcctIdx] = useState(0);
  const ch = channels.find((c) => c.id === chId) || channels[0];
  if (!ch) return null;
  const acct = ch.accounts[acctIdx] || ch.accounts[0];

  const totalChannels = channels.length;
  const activeChannels = channels.filter((c) => c.status === "ACTIVE").length;
  const total30d = channels.reduce((s, c) => s + (parseFloat(c.vol_30d_usd_eq) || 0), 0);

  return (
    <div style={{ display:"grid", gridTemplateColumns:"360px 1fr 380px", height:"calc(100vh - 40px - 30px)", minHeight:0 }}>
      {/* LEFT — channels list */}
      <div className="term-pane">
        <div className="term-pane-head">
          Channels <span className="meta"><b>{activeChannels}</b> active · {totalChannels} total</span>
        </div>
        <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line)", display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div>
            <div style={{ fontSize:9, letterSpacing:"0.14em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600 }}>30D RECEIVED</div>
            <div className="mono" style={{ fontSize:18, color:"var(--term-ink-1)", marginTop:2, letterSpacing:"-0.015em" }}>${(total30d / 1_000_000).toFixed(2)}M</div>
          </div>
          <button style={{ alignSelf:"end", padding:"8px 10px", background:"var(--term-green)", border:"1px solid var(--term-green)", color:"#000", fontWeight:600, letterSpacing:"0.04em", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>+ New channel</button>
        </div>
        <div className="term-pane-body">
          {channels.map((c) => {
            const active = c.id === chId;
            const vol = parseFloat(c.vol_30d_usd_eq) || 0;
            return (
              <div
                key={c.id}
                onClick={() => { setChId(c.id); setAcctIdx(0); }}
                style={{
                  position:"relative", padding:"12px 14px", borderBottom:"1px solid var(--term-line-dim)",
                  cursor:"pointer", background: active ? "var(--term-hover-bg)" : "transparent",
                }}
              >
                {active && <span style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"var(--term-green)" }} />}
                <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8 }}>
                  <span style={{ fontSize:13, fontWeight:500, letterSpacing:"-0.005em", color:"var(--term-ink-1)" }}>{c.client}</span>
                  <StatusPip status={c.status} />
                </div>
                <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginTop:3, gap:8 }}>
                  <span style={{ fontSize:11, color:"var(--term-ink-2)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.label}</span>
                  <span className="mono" style={{ fontSize:9, color:"var(--term-ink-3)", letterSpacing:"0.06em" }}>{c.id}</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8 }}>
                  <span style={{ display:"flex", gap:4 }}>
                    {Array.from(new Set(c.accounts.map((a) => a.kind))).map((k, i) => <KindPip key={i} kind={k} />)}
                    <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", alignSelf:"center", marginLeft:4 }}>{c.accounts.length} acct</span>
                  </span>
                  <span className="mono" style={{ fontSize:11, color:"var(--term-ink-1)" }}>${(vol / 1000).toFixed(0)}K</span>
                </div>
                <div className="mono" style={{ display:"flex", justifyContent:"space-between", marginTop:4, fontSize:9.5, color:"var(--term-ink-3)" }}>
                  <span>{c.received_30d_count} pmts · 30d</span>
                  <span>last {c.last_seen_iso}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER — channel detail */}
      <div className="term-pane">
        <div className="term-pane-head" style={{ height:"auto", alignItems:"flex-start", flexDirection:"column", gap:4, padding:"14px 18px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, width:"100%" }}>
            <span style={{ fontSize:16, fontWeight:500, letterSpacing:"-0.01em", color:"var(--term-ink-1)" }}>{ch.client}</span>
            <StatusPip status={ch.status} />
            <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", marginLeft:"auto", letterSpacing:"0.06em" }}>{ch.id}</span>
          </div>
          <div style={{ fontSize:11.5, color:"var(--term-ink-2)", textTransform:"none", letterSpacing:"normal", fontWeight:400 }}>{ch.label}</div>
          {ch.note && <div className="mono" style={{ fontSize:10.5, color:"#4A8CFF", textTransform:"none", letterSpacing:"normal", fontWeight:400, marginTop:2 }}>↳ {ch.note}</div>}
        </div>

        <div style={{ padding:"12px 18px", borderBottom:"1px solid var(--term-line)", display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
          <Stat label="30D VOLUME"     value={`$${((parseFloat(ch.vol_30d_usd_eq) || 0) / 1000).toFixed(0)}K`} mono />
          <Stat label="PAYMENTS · 30D" value={ch.received_30d_count} mono />
          <Stat label="ACCOUNTS"       value={ch.accounts.length} mono />
          <Stat label="LAST RECEIVED"  value={ch.last_seen_iso} mono small />
        </div>

        <div style={{ padding:"12px 18px 4px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:10, letterSpacing:"0.18em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600 }}>RECEIVING ACCOUNTS · {ch.accounts.length}</span>
          <button className="term-util-btn">+ Attach account</button>
        </div>
        <div style={{ padding:"0 18px 14px", display:"flex", flexDirection:"column", gap:6 }}>
          {ch.accounts.map((a, i) => {
            const sel = i === acctIdx;
            return (
              <div key={a.id} onClick={() => setAcctIdx(i)} style={{
                display:"grid", gridTemplateColumns:"auto 1fr auto auto", gap:12, alignItems:"center",
                padding:"10px 12px",
                border:`1px solid ${sel ? "var(--term-green)" : "var(--term-line)"}`,
                background: sel ? "rgba(44,203,126,0.05)" : "var(--term-bg-pane)",
                cursor:"pointer",
              }}>
                <KindPip kind={a.kind} />
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:12.5, color:"var(--term-ink-1)" }}>
                    {a.label}
                    {a.primary && <span style={{ marginLeft:8, fontSize:9, padding:"1px 5px", border:"1px solid var(--term-ink-3)", color:"var(--term-ink-3)", letterSpacing:"0.10em" }}>PRIMARY</span>}
                  </div>
                  <div className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", marginTop:2 }}>
                    {a.institution} · {a.number_masked}
                  </div>
                </div>
                <span className="mono" style={{ fontSize:10, color:"var(--term-ink-3)", letterSpacing:"0.06em" }}>{a.currency}</span>
                <span className="mono" style={{ fontSize:12, color:"var(--term-ink-1)" }}>
                  {(parseFloat(a.balance) || 0) > 0 ? `${((parseFloat(a.balance) || 0) / 1000).toFixed((parseFloat(a.balance) || 0) > 1_000_000 ? 0 : 1)}K` : "—"}
                </span>
              </div>
            );
          })}
        </div>

        {/* SHARE + payment-link */}
        <div style={{ padding:"14px 18px", borderTop:"1px solid var(--term-line)", flex:1, overflow:"auto" }}>
          <div style={{ fontSize:10, letterSpacing:"0.18em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:10 }}>SHARE · {acct.label}</div>
          <div style={{ display:"grid", gridTemplateColumns:"120px 1fr", gap:14 }}>
            <div style={{ width:120, height:120, background:"var(--term-ink-1)", display:"grid", gridTemplateColumns:"repeat(17, 1fr)", gridTemplateRows:"repeat(17, 1fr)", padding:6 }}>
              {Array.from({ length:289 }).map((_, i) => {
                const r = Math.floor(i / 17), c = i % 17;
                const seed = (acct.number_masked || "").charCodeAt(0) + (acct.institution || "").charCodeAt(0);
                const isFinder = (r < 5 && (c < 5 || c > 11)) || (r > 11 && c < 5);
                const finderOn = isFinder && ((r === 0 || r === 4 || r === 12 || r === 16) || (c === 0 || c === 4 || c === 12 || c === 16) || (r >= 1 && r <= 3 && c >= 1 && c <= 3) || (r >= 1 && r <= 3 && c >= 13 && c <= 15) || (r >= 13 && r <= 15 && c >= 1 && c <= 3));
                const dataOn = !isFinder && ((r * 7 + c * 13 + seed) % 3 === 0);
                return <span key={i} style={{ background: (finderOn || dataOn) ? "#07090E" : "transparent" }} />;
              })}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <FieldRow label={acct.kind === "WALLET" ? "NETWORK" : acct.kind === "CARD" ? "PROCESSOR" : "BANK"} value={acct.institution} />
              <FieldRow label={acct.kind === "WALLET" ? "ADDRESS" : acct.kind === "CARD" ? "ACCOUNT" : "ACCOUNT NUMBER"} value={acct.number_masked} mono copy />
              {acct.kind !== "CARD" && (
                <FieldRow label={acct.currency === "USD" ? "ROUTING (ABA)" : acct.kind === "WALLET" ? "CHAIN" : "BIC / SWIFT"} value={acct.routing} mono copy />
              )}
              <div style={{ display:"flex", gap:6, marginTop:4, flexWrap:"wrap" }}>
                <button className="term-util-btn">Copy details</button>
                <button className="term-util-btn">Email</button>
                <button className="term-util-btn">PDF letter</button>
                <button className="term-util-btn">Set primary</button>
              </div>
            </div>
          </div>

          <div style={{ marginTop:18, paddingTop:14, borderTop:"1px solid var(--term-line-dim)" }}>
            <div style={{ fontSize:10, letterSpacing:"0.18em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>PAYMENT LINK · CHANNEL-WIDE</div>
            <div style={{ display:"grid", gridTemplateColumns:"120px 1fr 110px", gap:8 }}>
              <div style={{ padding:"10px 12px", border:"1px solid var(--term-line)", background:"var(--term-bg-pane)", display:"flex", alignItems:"baseline", gap:6 }}>
                <input defaultValue="124,800.00" style={{ background:"transparent", border:"none", color:"var(--term-ink-1)", fontFamily:"JetBrains Mono", fontSize:13, width:"100%", outline:"none" }} />
                <span className="mono" style={{ fontSize:11, color:"var(--term-ink-3)" }}>{acct.currency}</span>
              </div>
              <div style={{ padding:"10px 12px", border:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
                <input defaultValue={`INV-${ch.id} · ${ch.label}`} style={{ background:"transparent", border:"none", color:"var(--term-ink-1)", fontSize:13, width:"100%", outline:"none" }} />
              </div>
              <button style={{ background:"var(--term-green)", border:"1px solid var(--term-green)", color:"#000", fontWeight:600, letterSpacing:"0.04em", fontSize:11.5, cursor:"pointer", fontFamily:"inherit" }}>Generate link</button>
            </div>
            <div className="mono" style={{ fontSize:11, color:"#4A8CFF", marginTop:10, padding:"8px 12px", background:"rgba(74,140,255,0.06)", border:"1px solid rgba(74,140,255,0.2)" }}>
              blox.global/pay/{ch.id.toLowerCase()} · payer picks rail · accepts {Array.from(new Set(ch.accounts.map((a) => a.currency))).join(", ")}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — channel incoming */}
      <div className="term-pane">
        <div className="term-pane-head">
          {ch.client.split(" ")[0]} · incoming
          <span className="meta"><span className="dot" />live</span>
        </div>
        <div style={{ padding:14, borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
          <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600 }}>RECEIVED · 30D · USD-EQ</div>
          <div className="mono" style={{ fontSize:22, color:"var(--term-ink-1)", fontWeight:500, letterSpacing:"-0.015em", lineHeight:1, marginTop:4 }}>${((parseFloat(ch.vol_30d_usd_eq) || 0) / 1000).toFixed(0)}K</div>
        </div>
        <div className="term-pane-body">
          <div style={{ display:"grid", gridTemplateColumns:"46px 60px 1fr 84px", gap:8, alignItems:"center", padding:"0 14px", height:22, fontSize:9.5, letterSpacing:"0.12em", color:"var(--term-ink-2)", textTransform:"uppercase", background:"var(--term-bg)", position:"sticky", top:0, zIndex:1, fontWeight:600, borderBottom:"1px solid var(--term-line-dim)" }}>
            <span>TIME</span><span>RAIL</span><span>ACCOUNT · REF</span><span style={{ textAlign:"right" }}>AMT</span>
          </div>
          {(incoming[ch.id] || []).length === 0 && (
            <div className="mono" style={{ padding:"24px 14px", fontSize:11.5, color:"var(--term-ink-3)", textAlign:"center" }}>
              no incoming · this channel is {ch.status.toLowerCase()}
            </div>
          )}
          {(incoming[ch.id] || []).map((tx, i) => {
            const cleared = tx.status === "CLEARED";
            return (
              <div key={i} className="term-tx-row term-has-ctx" style={{ gridTemplateColumns:"46px 60px 1fr 84px", position:"relative" }}>
                <span className="mono" style={{ fontSize:10.5, color:"var(--term-ink-3)" }}>{tx.when}</span>
                <RailTag rail={tx.rail} />
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:11.5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tx.account_label}</div>
                  <div className="mono" style={{ fontSize:9.5, color: cleared ? "var(--term-ink-3)" : "var(--term-amber)" }}>{tx.reference} · {tx.status}</div>
                </div>
                <span className="mono" style={{ textAlign:"right", fontSize:11, color:"var(--term-ink-1)" }}>+{fmtCcy(tx.amount, tx.currency)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ borderTop:"1px solid var(--term-line)", padding:"10px 14px", display:"flex", gap:8 }}>
          <button className="term-util-btn" style={{ flex:1 }}>Edit channel</button>
          <button className="term-util-btn" style={{ flex:1 }}>Routing rules</button>
          {ch.status === "ACTIVE"
            ? <button className="term-util-btn" style={{ flex:1, color:"var(--term-amber)", borderColor:"var(--term-amber)" }}>Pause</button>
            : <button className="term-util-btn" style={{ flex:1, color:"var(--term-green)", borderColor:"var(--term-green)" }}>Activate</button>}
        </div>
      </div>
    </div>
  );
}
